import React from "react";
import { RootState, getPayload } from "redux/store";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./styles";
import { ColumnLabel } from "component/shared/column-label";
import { createCampaign, updateCampaign, } from "redux/slice/campaigns";
import { createCampaignMessage } from "redux/slice/campaign-messages";
import { createCampaignCampaignMessage } from "redux/slice/campaign-campaign-messages";
import { getCampaignRoutes } from "redux/selector";
import { updateCampaignRoute } from "redux/slice/campaign-routes";
import { Campaign } from "model/campaign";
import { CampaignStatus } from "type/campaign-status";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { getCampaignSubscriptionsForCampaign } from "redux/selector";
import { EmailMarketingType } from "type/email-marketing-type";
import { AppCard } from "component/shared/app-card";
import { Dialog } from "component/shared/dialog";
import { WarningOutlined, Close } from "@mui/icons-material";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	const subscriptions = getCampaignSubscriptionsForCampaign(state, ownProps.campaign.id);
	const routes = getCampaignRoutes(state);
	return {
		subscriptions,
		routes,
	}
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	createCampaign, 
	updateCampaign,
	createCampaignMessage,
	createCampaignCampaignMessage,
	updateCampaignRoute,
}, dispatch);

interface OwnProps {
	campaign: Campaign;

}

type Props = OwnProps &
	WithStyles<typeof styles> & 
	WithMediaQuery &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps>;

interface State {
	disabled: boolean;
	activateModalOpen: boolean;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			disabled: false,
			activateModalOpen: false,
		}
	}

	private async handleCopy(campaign: Campaign) {
		const campaignCampaignMessages = campaign.campaignCampaignMessages;
		const {createCampaign, createCampaignMessage, createCampaignCampaignMessage} = this.props;
		const newCampaign = getPayload(await createCampaign({ campaign: {
			...campaign,
			name: campaign.name + " (Copy)",
			emailMarketingType: EmailMarketingType.DRIP,
		}}));
		for (let i=0; i < campaignCampaignMessages.length; i++ ) {
			const newCampaignMessage = getPayload(await createCampaignMessage({ campaignMessage: {
				...campaignCampaignMessages[i].campaignMessage,
			}}));
			if (newCampaignMessage.id && newCampaign.id) {
				getPayload(await createCampaignCampaignMessage({ campaignCampaignMessage: {
					campaignMessageId: newCampaignMessage.id,
					campaignId: newCampaign.id,
					messageOrder: campaignCampaignMessages[i].messageOrder,
				}}));
			}	
		}
	}

	private handleDelete(campaign: Campaign) {
		const { routes, updateCampaignRoute } = this.props;
		// MAX-3857: deactivate rather than delete, so the automation still shows (as "No Campaign").
		if (window.confirm("Delete this campaign? Any associated Automation rules will be deactivated.")) {
			routes.forEach((route) => {
				if (route.campaign?.id === campaign.id) {
					updateCampaignRoute({ campaignRoute: { ...route, inactive: true } });
				}
			});
			campaign.inactiveYn = true;
			this.props.updateCampaign({campaign});
		}
	}

	private handleToggleActivate(campaign: Campaign) {
		if (campaign.status === CampaignStatus.ACTIVE) {
			campaign.status = CampaignStatus.INACTIVE;
			this.props.updateCampaign({campaign});
		} else if (campaign.status === CampaignStatus.INACTIVE) {
			this.setState({ activateModalOpen: true });
		}
	}

	private handleConfirmActivate() {
		const { campaign } = this.props;
		campaign.status = CampaignStatus.ACTIVE;
		this.props.updateCampaign({campaign});
		this.setState({ activateModalOpen: false });
	}

	public render() {
		const { campaign, classes = {} } = this.props;
		const { activateModalOpen } = this.state;
		const isActive = campaign.status === CampaignStatus.ACTIVE;
		return (
			<>
				<Mui.Grid container spacing={5}>
					<Mui.Grid item xs={12}>
						<AppCard><Mui.CardContent>
								<Mui.Grid container direction="row" className={classes.container} spacing={2}>
									<Mui.Grid item lg={3} xs={12}>
										<Mui.Grid container alignItems="center" spacing={1}>
											<Mui.Grid item>
												<RouterLinkWrapper className={classes.title} style={{ whiteSpace: "nowrap"}} to={urls.campaign(campaign)}>
													<Mui.Typography variant="h4" color="primary">
														{campaign.name}
													</Mui.Typography>
												</RouterLinkWrapper>
											</Mui.Grid>
											<Mui.Grid item>
												<Mui.Chip
													size="small"
													label={isActive ? "Active" : "Inactive"}
													style={{
														backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
														color: "#fff",
													}}
												/>
											</Mui.Grid>
										</Mui.Grid>
									</Mui.Grid>
									<Mui.Grid item lg={5} md={6} sm={12} xs={12}>
										{this.stats()}
									</Mui.Grid>
									<Mui.Grid item style={{backgroundColor: "#fff"}} lg={4} md={6} sm={12} xs={12}>
										{this.buttons()}
									</Mui.Grid>
								</Mui.Grid>
						</Mui.CardContent></AppCard>
					</Mui.Grid>
				</Mui.Grid>
				<Dialog open={activateModalOpen} onClose={() => this.setState({ activateModalOpen: false })} maxWidth="sm" fullWidth>
					<Mui.DialogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
							<WarningOutlined style={{ fontSize: 28, color: "#f5a623" }} />
							<Mui.Typography variant="h4" component="h2">Before You Activate This Campaign</Mui.Typography>
						</Mui.Box>
						<Mui.IconButton onClick={() => this.setState({ activateModalOpen: false })}>
							<Close />
						</Mui.IconButton>
					</Mui.DialogTitle>
					<Mui.DialogContent>
						<Mui.Typography gutterBottom>
							Once activated, this campaign will begin sending to any subscribers you've added, based on the timing you've configured. Before you continue, make sure you've:
						</Mui.Typography>
						<ul>
							<li><Mui.Typography>Reviewed and customized each stage message</Mui.Typography></li>
							<li><Mui.Typography>Confirmed your campaign settings are correct</Mui.Typography></li>
							<li><Mui.Typography>Verified the right leads have been added as subscribers</Mui.Typography></li>
						</ul>
					</Mui.DialogContent>
					<Mui.DialogActions>
						<Mui.Button
							variant="outlined"
							color="primary"
							onClick={() => this.setState({ activateModalOpen: false })}
						>
							Cancel
						</Mui.Button>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => this.handleConfirmActivate()}
						>
							Activate Campaign
						</Mui.Button>
					</Mui.DialogActions>
				</Dialog>
			</>
		)
	}

	private stats() {
		const { campaign, classes = {}, mediaQuery, subscriptions } = this.props;
		const subscribes = subscriptions.length;
		return (
			<Mui.Grid container className={classes.stats} direction="row">
				<Mui.Grid item xs={3}>
					<Mui.Grid container direction="column">
						<Mui.Grid item className={classes.stat} xs={12}>
							{campaign.campaignStat ? campaign.campaignStat.sents : "0"}
						</Mui.Grid>
						<Mui.Grid item className={classes.stat} xs={12}>
							<ColumnLabel>
								Sent
							</ColumnLabel>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item xs={3} sm={3} md={4}>
					<Mui.Grid container direction="column">
						<Mui.Grid item className={classes.stat} xs={12}>
						{subscribes ? subscribes : "0"}
						</Mui.Grid>
						<Mui.Grid item className={classes.stat} xs={12}>
							<ColumnLabel>
								{mediaQuery && 
									<>Subs</>
								}
								{!mediaQuery &&
									<>Subscribers</>
								}
								
							</ColumnLabel>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item xs={3} md={3}>
					<Mui.Grid container direction="column">
						<Mui.Grid item className={classes.stat} xs={12}>
						{campaign.campaignStat && campaign.campaignStat.opens ? campaign.campaignStat.opens : "0"}
						</Mui.Grid>
						<Mui.Grid item className={classes.stat} xs={12}>
							<ColumnLabel>
								Opens
							</ColumnLabel>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item xs={3} sm={3} md={2}>
					<Mui.Grid container direction="column">
						<Mui.Grid item className={classes.stat} xs={12}>
							{campaign.campaignStat && campaign.campaignStat.clicks ? campaign.campaignStat.clicks : "0"}
						</Mui.Grid>
						<Mui.Grid item className={classes.stat} xs={12}>
							<ColumnLabel>
								Links
							</ColumnLabel>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>
		);
	}

	private buttons() {
		const { campaign, classes = {}, mediaQuery } = this.props;
		
		return (
			<Mui.Grid container direction={mediaQuery ? "column" : "row"} spacing={1} justifyContent={mediaQuery ? "center" : "flex-end"} wrap="nowrap">
				<Mui.Grid item >
					<Mui.Button
						color="primary"
						variant="contained"
						className={classes.actionButtons}
						onClick={() => this.handleCopy(campaign)}
						fullWidth={mediaQuery ? true : false}
					>
						Copy
					</Mui.Button>
				</Mui.Grid>

				<Mui.Grid item >
					<Mui.Button
						color="primary"
						variant="contained"
						className={classes.actionButtons}
						onClick={() => this.handleDelete(campaign)}
						fullWidth={mediaQuery ? true : false}
					>
						Delete
					</Mui.Button>
				</Mui.Grid>

				<Mui.Grid item >
					<Mui.Button
						color="primary"
						variant="contained"
						className={classes.actionButtons}
						onClick={() => this.handleToggleActivate(campaign)}
						fullWidth={mediaQuery ? true : false}
					>
						{campaign.status === CampaignStatus.ACTIVE ?
								<>Deactivate</> : <>Activate</>
						} 
					</Mui.Button>
				</Mui.Grid>
					
			</Mui.Grid>
		);
	}

}

export const Card = withMediaQuery("(max-width:375px)")(withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles));